import { useState, useEffect } from "react";
import { useNavigate, useParams } from 'react-router-dom';
import "./ContactDetail.css";
import { ArrowBackIos } from '@mui/icons-material';
import {
  IconButton, CircularProgress, Typography, Button, useTheme, Box, Chip,
  LinearProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Alert, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import { tokens } from "../../theme";
import { api } from '../../services/auth';
import { useBrand } from '../../contexts/BrandContext';
import { loyaltyFetch, LOYALTY_API_BASE } from '../fidelite/hooks/loyaltyFetch';

export default function ContactDetail() {
  const [activeTab, setActiveTab] = useState("profilUnomi");
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const loyaltyModuleActive = true;
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // ── Brand context (needed for loyalty API) ──────────────────
  const { currentBrand } = useBrand();
  const brandName = currentBrand?.name || "";

  // ── Loyalty state ────────────────────────────────────────────
  const [loyaltyWallet, setLoyaltyWallet] = useState(null);
  const [loyaltyCustomer, setLoyaltyCustomer] = useState(null);
  const [loyaltyInfo, setLoyaltyInfo] = useState(null);
  const [loyaltyMovements, setLoyaltyMovements] = useState([]);
  const [loyaltyRewards, setLoyaltyRewards] = useState([]);
  const [rewardDefs, setRewardDefs] = useState({});
  const [loyaltyLoading, setLoyaltyLoading] = useState(false);
  const [loyaltyError, setLoyaltyError] = useState(null);
  const [loyaltyFetched, setLoyaltyFetched] = useState(false);

  // ── Transactions state ───────────────────────────────────────
  const [transactions, setTransactions] = useState([]);
  const [transactionTypes, setTransactionTypes] = useState([]);
  const [selectedTxType, setSelectedTxType] = useState("all");
  const [txLoading, setTxLoading] = useState(false);
  const [txError, setTxError] = useState(null);
  const [txFetched, setTxFetched] = useState(false);

  const tabs = [
    { id: "profilUnomi", label: "Profil Unomi" },
    { id: "engagementMautic", label: "Engagement Email" },
    { id: "activiteUnomi", label: "Activité & Événements" },
  ];

  if (loyaltyModuleActive) {
    tabs.push({ id: "fideliteDetails", label: "Fidélité Détaillée" });
  }

  // ── Fetch Unomi contact ──────────────────────────────────────
  const fetchContactDetails = async (contactId) => {
    setLoading(true);
    try {
      const response = await api.get(`/profiles/${contactId}`);
      setContact(response.data.profile);
    } catch (error) {
      console.error('Erreur lors du chargement des détails:', error);
    } finally {
      setLoading(false);
    }
  };

  const { id } = useParams();
  const profileIdForLoyalty = contact?.itemId || id || "";

  useEffect(() => {
    if (id) {
      fetchContactDetails(id);
    }
  }, [id]);

  // Reset loyalty state when profile or brand changes so data is re-fetched correctly.
  useEffect(() => {
    setLoyaltyWallet(null);
    setLoyaltyCustomer(null);
    setLoyaltyInfo(null);
    setLoyaltyMovements([]);
    setLoyaltyRewards([]);
    setRewardDefs({});
    setLoyaltyError(null);
    setLoyaltyFetched(false);
  }, [profileIdForLoyalty, brandName]);

  // ── Fetch loyalty data (lazy — only when tab becomes active) ──
  const fetchLoyaltyData = async (profileId) => {
    if (!profileId || !brandName) return;
    setLoyaltyLoading(true);
    setLoyaltyError(null);
    const headers = { "X-Brand": brandName };

    try {
      // Base customer record (points balance + expiry dates)
      const cRes = await loyaltyFetch(
        `${LOYALTY_API_BASE}/customers/${encodeURIComponent(brandName)}/${encodeURIComponent(profileId)}`,
        { headers }
      );
      if (!cRes.ok) {
        if (cRes.status === 404) {
          setLoyaltyError("NOT_FOUND");
          return;
        }
        throw new Error("Erreur de récupération du profil client fidélité.");
      }
      const customerData = await cRes.json();
      setLoyaltyCustomer(customerData);
      setLoyaltyWallet({
        pointsBalance: customerData?.points_balance ?? 0,
        loyaltyStatus: customerData?.loyalty_status ?? null,
        lifetimePoints: customerData?.lifetime_points ?? 0,
      });

      // Loyalty tier details
      const lRes = await loyaltyFetch(
        `${LOYALTY_API_BASE}/customers/${encodeURIComponent(brandName)}/${encodeURIComponent(profileId)}/loyalty`,
        { headers }
      );
      if (lRes.ok) {
        setLoyaltyInfo(await lRes.json());
      }

      // Point movements
      const pmRes = await loyaltyFetch(
        `${LOYALTY_API_BASE}/customers/${encodeURIComponent(brandName)}/${encodeURIComponent(profileId)}/point-movements`,
        { headers }
      );
      if (pmRes.ok) setLoyaltyMovements(await pmRes.json());

      // Customer rewards
      const crRes = await loyaltyFetch(
        `${LOYALTY_API_BASE}/customers/${encodeURIComponent(brandName)}/${encodeURIComponent(profileId)}/rewards`,
        { headers }
      );
      let assignedRewards = [];
      if (crRes.ok) assignedRewards = await crRes.json();
      setLoyaltyRewards(assignedRewards);

      // Reward definitions (names) — only if there are rewards
      if (assignedRewards.length > 0) {
        const rRes = await loyaltyFetch(
          `${LOYALTY_API_BASE}/rewards?brand=${encodeURIComponent(brandName)}`,
          { headers }
        );
        if (rRes.ok) {
          const rData = await rRes.json();
          const defMap = {};
          rData.forEach(r => defMap[r.id] = r);
          setRewardDefs(defMap);
        }
      }
    } catch (err) {
      setLoyaltyError(err.message || "Erreur inconnue.");
    } finally {
      setLoyaltyLoading(false);
      setLoyaltyFetched(true);
    }
  };

  // ── Fetch Transactions (lazy — only when activiteUnomi tab becomes active) ──
  const fetchTransactions = async (profileId, type) => {
    if (!profileId || !brandName) return;
    setTxLoading(true);
    setTxError(null);
    const headers = { "X-Brand": brandName };

    try {
      if (transactionTypes.length === 0) {
        // Fetch available transaction types for the dropdown
        const typesRes = await loyaltyFetch(`${LOYALTY_API_BASE}/admin/transaction-types?brand=${encodeURIComponent(brandName)}`, { headers });
        if (typesRes.ok) {
          const typesData = await typesRes.json();
          setTransactionTypes(typesData);
        }
      }

      // Fetch transactions
      const endpoint = type === "all" || !type
        ? `${LOYALTY_API_BASE}/transactions/by-user?brand=${encodeURIComponent(brandName)}&profileId=${encodeURIComponent(profileId)}`
        : `${LOYALTY_API_BASE}/transactions/by-user-and-type?brand=${encodeURIComponent(brandName)}&profileId=${encodeURIComponent(profileId)}&transactionType=${encodeURIComponent(type)}`;

      const res = await loyaltyFetch(endpoint, { headers });
      if (!res.ok) throw new Error("Erreur lors du chargement des transactions");
      
      const data = await res.json();
      setTransactions(data);
    } catch (err) {
      setTxError(err.message || "Erreur inconnue");
    } finally {
      setTxLoading(false);
      setTxFetched(true);
    }
  };

  useEffect(() => {
    if (activeTab === "activiteUnomi" && !txFetched && id) {
      fetchTransactions(id, selectedTxType);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, txFetched, id]);

  const handleTxTypeChange = (e) => {
    const newType = e.target.value;
    setSelectedTxType(newType);
    if (id) {
      fetchTransactions(id, newType);
    }
  };

  // Trigger loyalty fetch when tab becomes active (only once per profile load)
  useEffect(() => {
    if (activeTab === "fideliteDetails" && !loyaltyFetched && profileIdForLoyalty && brandName) {
      fetchLoyaltyData(profileIdForLoyalty);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, loyaltyFetched, profileIdForLoyalty, brandName]);

  const handleBack = () => {
    navigate('/contacts');
  };

  // ── Helpers ──────────────────────────────────────────────────
  const getRewardStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'ISSUED': return 'primary';
      case 'USED': return 'default';
      case 'EXPIRED': return 'error';
      case 'CANCELLED': return 'warning';
      default: return 'default';
    }
  };

  const getRewardStatusLabel = (status) => {
    switch (status?.toUpperCase()) {
      case 'ISSUED': return 'Disponible';
      case 'USED': return 'Utilisé';
      case 'EXPIRED': return 'Expiré';
      case 'CANCELLED': return 'Annulé';
      default: return status;
    }
  };

  const fmtDate = (v) => v ? new Date(v).toLocaleDateString('fr-FR') : '–';
  const fmtDateTime = (v) => v ? new Date(v).toLocaleString('fr-FR') : '–';
  const getLastChangeLabel = (lastChange) => {
    switch (lastChange) {
      case "upgrade":
        return "Montée de niveau";
      case "downgrade":
        return "Rétrogradation";
      case "no_change":
        return "Renouvelé (pas de changement)";
      default:
        return "–";
    }
  };

  // Progress percentage toward next tier
  const tierProgress = loyaltyInfo?.nextTier
    ? Math.min(100, ((loyaltyInfo.statusPoints || 0) / loyaltyInfo.nextTier.minStatusPoints) * 100)
    : 100;

  return (
    <div className="contact-detail-page">
      <div className="page-header">
        <IconButton
          onClick={handleBack}
          className="back-button"
          size="small"
        >
          <ArrowBackIos />
        </IconButton>
        <h1>Détail Contact : {contact?.properties?.firstName} {contact?.properties?.lastName}</h1>
      </div>

      <div className="contact-card-grid">
        <aside className="contact-summary-panel">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <CircularProgress />
            </div>
          ) : contact ? (
            <>
              <div className="profile-pic">
                {contact.properties?.firstName?.[0] || ''}{contact.properties?.lastName?.[0] || ''}
              </div>
              <h2>{contact.properties?.firstName} {contact.properties?.lastName}</h2>
              <p className="contact-email">{contact.properties?.email}</p>

              <div className="info-block">
                <label>ID Unomi:</label>
                <span><code>{contact.itemId}</code></span>
              </div>
              <div className="info-block">
                <label>Statut:</label>
                <span>{contact.properties?.city || 'N/A'}</span>
              </div>
              <div className="info-block">
                <label>Dernière Activité:</label>
                <span>
                  {contact.properties?.lastVisit
                    ? new Date(contact.properties.lastVisit).toLocaleString('fr-FR', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        timeZone: 'UTC'
                      })
                    : 'N/A'}
                </span>
              </div>

              {loyaltyModuleActive && (
                <div className="info-block">
                  <label>Niveau Fidélité:</label>
                  <span style={{ fontWeight: "bold", color: "#f39c12" }}>
                    {loyaltyInfo?.currentTier?.name || loyaltyCustomer?.loyalty_status_name || loyaltyWallet?.loyaltyStatus || 'N/A'}
                  </span><br />
                  <label style={{ marginTop: "5px" }}>Points Fidélité:</label>
                  <span>
                    {loyaltyWallet != null ? `${loyaltyWallet.pointsBalance ?? 0} pts` : `${loyaltyCustomer?.points_balance ?? 0} pts`}
                  </span>
                </div>
              )}

              <div className="info-block">
                <label>tags UNOMI:</label>
                <div className="tags-list">
                  {contact.tags?.map((segment, index) => (
                    <span key={index}>{segment || <li>Aucun tags</li>}</span>
                  ))}
                </div>
              </div>

              <div className="action-buttons-contact">
                <button className="email-direct-btn">Envoyer un Email Direct</button>
                <button className="add-to-campaign-btn">Ajouter à une Campagne</button>
                {loyaltyModuleActive && (
                  <button style={{ backgroundColor: "#9b59b6", color: "white" }}>
                    Ajuster Points Fidélité
                  </button>
                )}
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Typography variant="h6" color="error">
                Contact non trouvé
              </Typography>
            </div>
          )}
        </aside>

        <div className="contact-details-tabs">
          <nav className="tabs-nav">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`tab-link ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </nav>

          {/* ── Profil Unomi ──────────────────────────────────── */}
          <div className={`tab-content ${activeTab === "profilUnomi" ? "active" : ""}`}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <CircularProgress />
              </div>
            ) : contact ? (
              <>
                <h3>Propriétés du Profil (Unomi)</h3>
                <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '16px' }}>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Prénom</Typography>
                    <Typography variant="body1">{contact.properties?.firstName || 'N/A'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Nom</Typography>
                    <Typography variant="body1">{contact.properties?.lastName || 'N/A'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Email</Typography>
                    <Typography variant="body1">{contact.properties?.email || 'N/A'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Ville</Typography>
                    <Typography variant="body1">{contact.properties?.city || 'N/A'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Pays</Typography>
                    <Typography variant="body1">{contact.properties?.country || 'N/A'}</Typography>
                  </Box>
                </Box>

                <h3 style={{ marginTop: '24px' }}>Appartenance aux Segments (Unomi)</h3>
                {contact.segments && contact.segments.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {contact.segments.map((segment, index) => (
                      <Chip
                        key={index}
                        label={segment}
                        size="small"
                        sx={{
                          backgroundColor: colors.blueAccent[700],
                          color: 'white',
                        }}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                    Aucun segment
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="h6" color="error" style={{ textAlign: 'center', padding: '20px' }}>
                Contact non trouvé
              </Typography>
            )}
          </div>

          {/* ── Engagement Email ──────────────────────────────── */}
          <div className={`tab-content ${activeTab === "engagementMautic" ? "active" : ""}`}>
            <h3>Historique d'Engagement Email (Mautic)</h3>
            <ul className="timeline">
              <li className="timeline-item">
                <span className="time">2024-05-10 10:05</span>
                <div className="event-title">Clic sur Email : "Promo Spéciale VIP"</div>
                <div className="event-details">Lien cliqué : <code>https://site.com/promo-vip</code></div>
              </li>
              <li className="timeline-item">
                <span className="time">2024-05-10 09:30</span>
                <div className="event-title">Ouverture Email : "Promo Spéciale VIP"</div>
              </li>
              <li className="timeline-item">
                <span className="time">2024-04-20 14:00</span>
                <div className="event-title">Email Envoyé : "Newsletter Avril"</div>
                <div className="event-details">Statut : Ouvert, Non Cliqué</div>
              </li>
            </ul>
          </div>

          {/* ── Activité & Événements ─────────────────────────── */}
          <div className={`tab-content ${activeTab === "activiteUnomi" ? "active" : ""}`}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <h3 style={{ margin: 0 }}>Historique d'Activité et Événements (Unomi)</h3>
              <FormControl size="small" sx={{ minWidth: 200, bgcolor: 'white' }}>
                <InputLabel>Filtrer par type</InputLabel>
                <Select
                  value={selectedTxType}
                  label="Filtrer par type"
                  onChange={handleTxTypeChange}
                >
                  <MenuItem value="all">Tous les événements</MenuItem>
                  {transactionTypes.map(tt => (
                    <MenuItem key={tt.key || tt.id} value={tt.key || tt.id}>{tt.name || tt.key || tt.id}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {txLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress sx={{ color: "#3cb0d6" }} />
              </Box>
            ) : txError ? (
              <Alert severity="error" sx={{ mt: 2 }}>{txError}</Alert>
            ) : transactions.length === 0 ? (
              <Box py={4} textAlign="center" color="text.secondary">
                Aucun événement ou activité trouvé pour ce filtre.
              </Box>
            ) : (
              <ul className="timeline">
                {transactions.map(tx => (
                  <li className="timeline-item" key={tx.id}>
                    <span className="time">
                      {tx.created_at ? new Date(tx.created_at).toLocaleString('fr-FR', {
                        day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                      }) : 'N/A'}
                    </span>
                    <div className="event-title">
                      {tx.transaction_type}
                      {tx.source && (
                        <Chip label={tx.source} size="small" sx={{ ml: 1, fontSize: 10, height: 20, bgcolor: "#f0f9ff", color: "#0369a1" }} />
                      )}
                    </div>
                    {tx.payload && Object.keys(tx.payload).length > 0 && (
                      <div className="event-details">
                        <details>
                          <summary style={{ cursor: 'pointer', color: '#3cb0d6', fontWeight: 600 }}>Détails de l'événement</summary>
                          <pre style={{ fontSize: 11, background: '#f8f9ff', padding: 12, borderRadius: 6, overflowX: 'auto', mt: 1, border: '1px solid #e8ecf1' }}>
                            {JSON.stringify(tx.payload, null, 2)}
                          </pre>
                        </details>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* ── Fidélité Détaillée ────────────────────────────── */}
          {loyaltyModuleActive && (
            <div className={`tab-content ${activeTab === "fideliteDetails" ? "active" : ""}`}>
              <h3>Détails du Programme de Fidélité (SGF)</h3>

              {loyaltyLoading && (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress sx={{ color: "#3cb0d6" }} />
                </Box>
              )}

              {!loyaltyLoading && loyaltyError === "NOT_FOUND" && (
                <Alert severity="info" sx={{ borderRadius: 2, mt: 1 }}>
                  Ce contact n'a pas encore de profil dans le système de fidélité.
                  {!brandName && " (Aucune marque sélectionnée — vérifiez le sélecteur de marque.)"}
                </Alert>
              )}

              {!loyaltyLoading && loyaltyError && loyaltyError !== "NOT_FOUND" && (
                <Alert severity="error" sx={{ borderRadius: 2, mt: 1 }}>
                  {loyaltyError}
                </Alert>
              )}

              {!loyaltyLoading && !loyaltyError && loyaltyFetched && loyaltyWallet && (
                <>
                  {/* ── KPI Header ─────────────────────────────── */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))',
                      gap: 2,
                      mb: 3,
                      mt: 1,
                    }}
                  >
                    <Box textAlign="center" p={2} bgcolor="#fafbfd" borderRadius={2} border="1px solid #e8ecf1">
                      <Typography fontSize={11} color="text.secondary" fontWeight={600} mb={0.5} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Points Wallet
                      </Typography>
                      <Typography variant="h5" fontWeight={800} color="#3cb0d6">
                        {loyaltyWallet?.pointsBalance ?? loyaltyCustomer?.points_balance ?? 0}
                      </Typography>
                    </Box>

                    <Box textAlign="center" p={2} bgcolor="#fafbfd" borderRadius={2} border="1px solid #e8ecf1">
                      <Typography fontSize={11} color="text.secondary" fontWeight={600} mb={0.5} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Niveau
                      </Typography>
                      <Typography variant="h5" fontWeight={800} color="#FFD93D">
                        {loyaltyInfo?.currentTier?.name || loyaltyCustomer?.loyalty_status_name || loyaltyWallet?.loyaltyStatus || '–'}
                      </Typography>
                    </Box>

                    <Box textAlign="center" p={2} bgcolor="#fafbfd" borderRadius={2} border="1px solid #e8ecf1">
                      <Typography fontSize={11} color="text.secondary" fontWeight={600} mb={0.5} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Dernier changement
                      </Typography>
                      <Typography variant="body1" fontWeight={800} color="#475569">
                        {getLastChangeLabel(loyaltyInfo?.lastChange)}
                      </Typography>
                    </Box>
                  </Box>

                  {/* ── Dates d'expiration ─────────────────────── */}
                  {loyaltyCustomer && (
                    <Box mb={3} display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                      <Box px={2} py={1.5} bgcolor="#fafbfd" borderRadius={2} border="1px solid #e8ecf1">
                        <Typography fontSize={11} color="text.secondary" fontWeight={600} mb={0.5} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Expiration des Points
                        </Typography>
                        <Typography fontSize={14} fontWeight={700} color={loyaltyCustomer.points_expires_at ? '#DC2626' : '#64748b'}>
                          {loyaltyCustomer.points_expires_at ? fmtDate(loyaltyCustomer.points_expires_at) : '–'}
                        </Typography>
                      </Box>
                      <Box px={2} py={1.5} bgcolor="#fafbfd" borderRadius={2} border="1px solid #e8ecf1">
                        <Typography fontSize={11} color="text.secondary" fontWeight={600} mb={0.5} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Expiration du Statut
                        </Typography>
                        <Typography fontSize={14} fontWeight={700} color={loyaltyCustomer.loyalty_status_expires_at ? '#DC2626' : '#64748b'}>
                          {loyaltyCustomer.loyalty_status_expires_at ? fmtDate(loyaltyCustomer.loyalty_status_expires_at) : '–'}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* ── Tier Progression Bar ───────────────────── */}
                  {loyaltyInfo?.currentTier && (
                    <Box mb={3} p={2} bgcolor="#f8f9ff" borderRadius={2} border="1px solid #e8ecf1">
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography fontSize={13} fontWeight={600} color="#334155">
                          Progression — Niveau actuel : <strong>{loyaltyInfo.currentTier.name}</strong>
                        </Typography>
                        {loyaltyInfo.nextTier && (
                          <Typography fontSize={12} color="text.secondary">
                            {loyaltyInfo.pointsToNextTier ?? '?'} SP avant <strong>{loyaltyInfo.nextTier.name}</strong>
                          </Typography>
                        )}
                      </Box>
                      <Box display="flex" alignItems="center" gap={2}>
                        <LinearProgress
                          variant="determinate"
                          value={tierProgress}
                          sx={{
                            flexGrow: 1,
                            height: 8,
                            borderRadius: 4,
                            bgcolor: '#e2e8f0',
                            '& .MuiLinearProgress-bar': { bgcolor: '#3cb0d6' },
                          }}
                        />
                        <Typography variant="caption" fontWeight={600} color="#3cb0d6">
                          {loyaltyInfo.statusPoints} SP
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* ── Avantages & Vouchers ───────────────────── */}
                  <Typography variant="h6" fontWeight={700} mb={1.5} color="#1a1a2e" fontSize={15}>
                    Avantages & Vouchers ({loyaltyRewards.length})
                  </Typography>
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{ mb: 3, borderRadius: 2, border: "1px solid #e8ecf1" }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f8f9ff' }}>
                          <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Récompense</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Statut</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Obtenu le</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Expire le</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Utilisé le</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loyaltyRewards.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary', fontStyle: 'italic' }}>
                              Aucune récompense active.
                            </TableCell>
                          </TableRow>
                        ) : loyaltyRewards.map((row) => {
                          const def = rewardDefs[row.reward_id];
                          return (
                            <TableRow key={row.id} hover>
                              <TableCell>
                                <Typography fontWeight={600} fontSize={13}>
                                  {def ? def.name : row.reward_id}
                                </Typography>
                                {def?.description && (
                                  <Typography fontSize={11} color="text.secondary">{def.description}</Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={getRewardStatusLabel(row.status)}
                                  color={getRewardStatusColor(row.status)}
                                  size="small"
                                  sx={{ fontWeight: 600, fontSize: 11 }}
                                />
                              </TableCell>
                              <TableCell sx={{ fontSize: 13 }}>{fmtDate(row.issued_at)}</TableCell>
                              <TableCell sx={{ fontSize: 13 }}>{row.expires_at ? fmtDate(row.expires_at) : 'Jamais'}</TableCell>
                              <TableCell sx={{ fontSize: 13 }}>{fmtDate(row.used_at)}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* ── Historique des Points ──────────────────── */}
                  <Typography variant="h6" fontWeight={700} mb={1.5} color="#1a1a2e" fontSize={15}>
                    Historique des Points ({loyaltyMovements.length})
                  </Typography>
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{ borderRadius: 2, border: "1px solid #e8ecf1" }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f8f9ff' }}>
                          <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Type</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Variation</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loyaltyMovements.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={3} align="center" sx={{ py: 3, color: 'text.secondary', fontStyle: 'italic' }}>
                              Aucun mouvement enregistré.
                            </TableCell>
                          </TableRow>
                        ) : loyaltyMovements.map((row) => (
                          <TableRow key={row.id} hover>
                            <TableCell sx={{ fontSize: 13 }}>{fmtDateTime(row.created_at)}</TableCell>
                            <TableCell>
                              <Chip
                                label={row.type}
                                size="small"
                                variant="outlined"
                                sx={{ fontWeight: 600, fontSize: 11, borderRadius: 1 }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography
                                fontWeight={700}
                                fontSize={13}
                                color={
                                  row.points > 0
                                    ? 'success.main'
                                    : row.points < 0
                                    ? 'error.main'
                                    : 'text.secondary'
                                }
                              >
                                {row.points > 0 ? `+${row.points}` : row.points}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
